import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ChippedMultiSelect from 'components/shared/ChippedMultiSelect';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import Input from 'components/shared/form/Input';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import Upload from 'hooks/useDirectUpload/Upload';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { GroupAccessMode, groupsQueryKeys, indexGroups } from 'services/api/group';
import { createWorkflow, updateWorkflow, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, H3 } from 'styles/typography';

import { DEMOGRAPHIC_FIELDS, INBOUND_REFERRALS_ROUTES, REFERRAL_INTAKE_API_NAME, VALUE_NAMES } from './constants';

interface Props {
  // Present in edit mode; absent when creating.
  workflow?: Workflow;
}

// A building option / selection — the shape both the group index and the workflow's
// own groups share.
type BuildingOption = { id: string; name: string };

// The text demographics (everything except the date), in declaration order.
const TEXT_FIELDS = DEMOGRAPHIC_FIELDS.filter((field) => field.type === 'text');

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

export default function ReferralForm({ workflow }: Props) {
  const navigate = useNavigate();
  const isEdit = !!workflow;

  const [selectedBuildings, setSelectedBuildings] = useState<BuildingOption[]>(
    workflow?.groups.map((group) => ({ id: group.id, name: group.name })) ?? []
  );

  // The values currently stored on the run, used both to seed the inputs and to send
  // only what actually changed.
  const stored = useMemo(() => {
    const text = Object.fromEntries(
      TEXT_FIELDS.map((field) => {
        return [field.name, workflow?.valueFor(field.name)?.value ?? ''];
      })
    );
    const dob = workflow?.valueFor(VALUE_NAMES.PATIENT_DOB)?.value ?? '';
    return { text, dob };
  }, [workflow]);
  const originalGroupIds = useMemo(() => workflow?.groups.map((group) => group.id) ?? [], [workflow]);

  const [text, setText] = useState<Record<string, string>>(stored.text);
  const [dob, setDob] = useState<Date | null>(parseDate(stored.dob) ?? null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const groupsParams = useMemo(
    () => ({ accessModes: [GroupAccessMode.Allowed], type: SKILLED_NURSING_FACILITY, pageSize: 200, sortBy: 'name' }),
    []
  );
  const { data: groupsResult, isPending: groupsLoading } = useQuery({
    queryKey: [...groupsQueryKeys.index, groupsParams],
    queryFn: ({ signal }) => indexGroups(groupsParams, signal),
  });
  const groups = groupsResult?.data ?? [];

  const goToReferral = (id: string) => navigate(`${INBOUND_REFERRALS_ROUTES.BASE}/${id}`);

  const { mutate: create, isPending: creating } = useMutateWithToast({
    mutationFn: createWorkflow,
    onSuccess: (created: Workflow) => goToReferral(created.id),
  });
  const { mutate: save, isPending: saving } = useMutateWithToast({
    mutationFn: updateWorkflow,
    onSuccess: () => workflow && goToReferral(workflow.id),
  });

  // Only the demographics whose value differs from what's stored — so an unchanged
  // edit sends nothing, and a cleared field sends '' to remove it.
  const changedValues = (): WorkflowValuesInput => {
    const changes: WorkflowValuesInput = {};
    TEXT_FIELDS.forEach((field) => {
      const next = text[field.name].trim();
      if (next !== stored.text[field.name]) changes[field.name] = next;
    });
    const nextDob = simpleDashDate(dob) ?? '';
    if (nextDob !== stored.dob) changes[VALUE_NAMES.PATIENT_DOB] = nextDob;
    return changes;
  };

  const groupIds = selectedBuildings.map((building) => building.id);
  const groupsChanged = isEdit && !sameIds(groupIds, originalGroupIds);
  const pendingValues = changedValues();
  const attachments = uploads.length > 0 ? uploads : undefined;
  const hasContent = Object.keys(pendingValues).length > 0 || !!attachments;
  // A referral always needs a building; beyond that, create needs a packet or a
  // demographic, and an edit needs something actually changed (the update rejects a
  // no-op write).
  const canSubmit = groupIds.length > 0 && (isEdit ? hasContent || groupsChanged : hasContent);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;

    if (isEdit) {
      save({ workflowId: workflow.id, groupIds: groupsChanged ? groupIds : undefined, values, attachments });
      return;
    }
    create({ workflowDefinitionApiName: REFERRAL_INTAKE_API_NAME, groupIds, values, attachments });
  };

  const submitting = creating || saving;

  return (
    <Form>
      <Field>
        <div data-testid='building-select'>
          <ChippedMultiSelect<BuildingOption>
            options={groups}
            value={selectedBuildings}
            onChange={setSelectedBuildings}
            getOptionValue={(building) => building.id}
            getOptionLabel={(building) => building.name}
            inputLabel='Buildings'
            containerLabel='Selected buildings'
            placeholder='Search buildings'
            closeMenuOnSelect={false}
          />
        </div>
        {!groupsLoading && groups.length === 0 && (
          <HelpText>You don’t have access to any buildings for inbound referrals.</HelpText>
        )}
      </Field>

      <div>
        <Subhead>Upload referral packet</Subhead>
        {workflow && workflow.attachments.length > 0 && (
          <CurrentPacket data-testid='current-packet'>
            Current packet:{' '}
            {workflow.attachments
              .map((attachment) => attachment.filename)
              .filter(Boolean)
              .join(', ')}
          </CurrentPacket>
        )}
        <DropZone data-testid='packet-dropzone'>
          <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
        </DropZone>
        {workflow && uploads.length > 0 && workflow.attachments.length > 0 && (
          <ReplaceWarning data-testid='packet-replace-warning'>
            Uploading will replace the current {workflow.attachments.length} document
            {workflow.attachments.length > 1 ? 's' : ''}.
          </ReplaceWarning>
        )}
      </div>

      {isEdit && (
        <div>
          <Subhead>Demographics</Subhead>
          <Body>The AI fills these from the packet — edit them if needed.</Body>
          <Fields>
            {DEMOGRAPHIC_FIELDS.map((field) => (
              <Field key={field.name}>
                <FieldLabel>{field.label}</FieldLabel>
                {field.type === 'date' ? (
                  <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />
                ) : (
                  <Input
                    value={text[field.name]}
                    onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    data-testid={`field-${field.name}`}
                  />
                )}
              </Field>
            ))}
          </Fields>
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!canSubmit || submitting || isUploading}
        loading={submitting}
        data-testid='save-referral'>
        Save
      </Button>
    </Form>
  );
}

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 640px;
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Field = styled.div`
  min-width: 200px;
`;

const Subhead = styled(H3)`
  margin-top: 8px;
`;

const DropZone = styled.div`
  margin-top: 8px;
`;

const CurrentPacket = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.black75};
`;

const ReplaceWarning = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  color: ${colors.orangeHover};
  font-weight: 500;
`;

const HelpText = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.red100};
`;
